import { useEffect, useState } from 'react';

/**
 * InlineDocViewer · portal-wide inline document viewing (Results Changes #2).
 *
 * Documents open INLINE beneath their row under an indented disclosure arrow
 * instead of a new tab. The frame points ONLY at /api/* asset URLs (the SWA
 * X-Frame-Options: DENY global header is not stamped on /api/* responses, and
 * /api is same-origin so the cdx_session cookie rides the iframe request; under
 * a future 2FA-on posture the caller passes an assetUrl()-wrapped src whose
 * ?pt= factor is FROZEN at mount so token rotation never reloads a PDF
 * mid-read). No `sandbox` attribute: a bare sandbox blanks Chromium's PDF
 * viewer. Lazy-mount the component only while disclosed so closed rows fetch
 * nothing (the asset endpoint is Cache-Control: no-store).
 *
 * Load failure (decision 7): no visible link normally; the error state shows a
 * small note WITH an "Open in new tab" escape hatch. The iframe itself gives no
 * HTTP-error signal, so a lightweight ranged GET preflight detects failures.
 */

const FRAME_HEIGHT = 540; // fixed: PDFs have no intrinsic height (house maxHeight convention)

export function InlineDocFrame({ url, title, sandboxed }: { url: string; title?: string | undefined; sandboxed?: boolean | undefined }) {
  // Freeze the src for the life of the disclosure (see header note).
  const [src] = useState(url);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // Reachability probe. The asset routes ignore Range and stream the whole
    // body, so the body is CANCELLED as soon as the status is known; without
    // that, every disclosure would download the document twice (no-store).
    fetch(src, { credentials: 'same-origin', headers: { Range: 'bytes=0-0' }, signal: controller.signal })
      .then((r) => {
        void r.body?.cancel().catch(() => { /* already-consumed/locked body: ignore */ });
        if (!cancelled && !(r.ok || r.status === 206)) setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [src]);

  if (failed) {
    return (
      <div className="px-3 py-2 bg-cdx-danger/5 border border-cdx-danger/20 rounded-cdx text-xs text-cdx-danger">
        Could not load the document.{' '}
        <a href={src} target="_blank" rel="noreferrer" className="underline">Open in new tab</a>
      </div>
    );
  }
  return (
    <iframe
      src={src}
      title={title ?? 'Document'}
      className="w-full border border-cdx-border-subtle rounded-cdx bg-white"
      style={{ height: FRAME_HEIGHT }}
      // Script-bearing previews (SVG is a document with script rights inside an
      // iframe) get an empty sandbox: rendering still works, scripts cannot run
      // in the portal origin. PDFs must stay UNsandboxed (a bare sandbox blanks
      // Chromium's PDF viewer), which is safe: the PDF viewer is browser chrome.
      {...(sandboxed ? { sandbox: '' } : {})}
    />
  );
}

/** The N4-standard boxed disclosure arrow that toggles an inline viewer. */
export function DocDisclosureButton({ open, onToggle, label }: { open: boolean; onToggle: () => void; label?: string | undefined }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-[12px] leading-none w-5 h-5 rounded border border-cdx-border-subtle hover:bg-cdx-surface-2"
      aria-expanded={open}
      aria-label={open ? `Hide ${label ?? 'document'}` : `View ${label ?? 'document'} inline`}
      title={open ? `Hide ${label ?? 'document'}` : `View ${label ?? 'document'} inline`}
    >
      {open ? '▾' : '▸'}
    </button>
  );
}

/** Indented wrapper for the disclosed viewer (opens underneath the parent row). */
export function DocDisclosurePanel({ children }: { children: React.ReactNode }) {
  return <div className="pl-6 py-2">{children}</div>;
}
